/* import __COLOCATED_TEMPLATE__ from './send-recovery-code-cell.hbs'; */
/* RESPONSIBLE TEAM: team-app-security */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency-decorators';

export default class SendRecoveryCodeCell extends Component {
  @service intercomEventService;
  @service notificationsService;
  @service appService;
  @service permissionsService;
  @service intl;
  @service intercomConfirmService;

  @tracked isOtpModalOpen = false;
  @tracked isOtpLoading = false;
  @tracked otpErrorMessage = '';

  get canSendRecoveryCode() {
    return (
      this.permissionsService.currentAdminCan('can_manage_teammates') &&
      this.admin !== this.appService.app.currentAdmin &&
      this.admin?.has_enabled_2fa
    );
  }

  get admin() {
    return this.args.row.teammate;
  }

  get currentUserHas2FA() {
    return this.appService.app.currentAdmin?.has_enabled_2fa;
  }

  get otpModalTitle() {
    return this.intl.t('table-cells.teammate-list.send-recovery-codes-cell.otp-modal-title');
  }

  get otpModalDescription() {
    return this.intl.t('table-cells.teammate-list.send-recovery-codes-cell.otp-modal-description', {
      email: this.admin?.email || '',
    });
  }

  get otpModalConfirmButtonText() {
    return this.intl.t('table-cells.teammate-list.send-recovery-codes-cell.send-codes');
  }

  @action
  async sendRecoveryCode() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'send_2_fa_recovery_codes',
      object: 'email_recovery_code',
      context: 'from_teammate_settings',
      models: [this.admin],
    });

    if (this.currentUserHas2FA) {
      this.showOtpModal();
    } else {
      await this.sendWithBasicConfirmation();
    }
  }

  @action
  showOtpModal() {
    this.isOtpModalOpen = true;
    this.otpErrorMessage = '';
  }

  @action
  closeOtpModal() {
    this.isOtpModalOpen = false;
    this.otpErrorMessage = '';
    this.isOtpLoading = false;
  }

  @action
  clearOtpError() {
    this.otpErrorMessage = '';
  }

  @action
  onOtpConfirm(otpCode) {
    this.sendRecoveryCodeWithOtp.perform(otpCode);
  }

  @action
  async sendWithBasicConfirmation() {
    let confirmOptions = {
      title: this.intl.t('table-cells.teammate-list.send-recovery-codes-cell.confirm-title'),
      body: this.intl.t('table-cells.teammate-list.send-recovery-codes-cell.confirm-description', {
        email: this.admin?.email || '',
      }),
      confirmButtonText: this.intl.t(
        'table-cells.teammate-list.send-recovery-codes-cell.send-codes',
      ),
      confirmButtonAttributes: {
        'data-test-send-recovery-confirm-btn': true,
      },
      modalAttributes: {
        'data-test-send-recovery-confirmation-modal': true,
      },
    };

    let isConfirmed = await this.intercomConfirmService.confirm(confirmOptions);
    if (!isConfirmed) {
      return;
    }

    try {
      await this.admin.sendRecoveryCodeEmail();
      this.notificationsService.notifyConfirmation(
        this.intl.t('table-cells.teammate-list.send-recovery-codes-cell.codes-sent', {
          email: this.admin?.email || '',
        }),
      );
    } catch (err) {
      this.notificationsService.notifyError(
        this.intl.t('table-cells.teammate-list.send-recovery-codes-cell.sending-failure'),
      );
    }
  }

  @dropTask
  *sendRecoveryCodeWithOtp(verificationCode) {
    this.isOtpLoading = true;
    this.otpErrorMessage = '';

    try {
      yield this.admin.sendRecoveryCodeEmail({ validation_code: verificationCode });

      this.closeOtpModal();
      this.notificationsService.notifyConfirmation(
        this.intl.t('table-cells.teammate-list.send-recovery-codes-cell.codes-sent', {
          email: this.admin?.email || '',
        }),
      );
    } catch (err) {
      this.isOtpLoading = false;
      if (err.jqXHR.status === 403) {
        this.otpErrorMessage = this.intl.t('apps.app.account.two-factor-auth.invalid-otp-code');
      } else {
        this.otpErrorMessage = this.intl.t(
          'table-cells.teammate-list.send-recovery-codes-cell.sending-failure',
        );
      }
    }
  }
}
